import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import type { DayOfWeek } from "@prisma/client";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";

import { type AreaSelection, AvailableTimes } from "@/components/AvailableTimes";
import { CustomDatePicker } from "@/components/CustomDatePicker";
import { GuestTotalLine } from "@/components/GuestTotalLine";
import { useBookingAnalytics } from "@/hooks/useBookingAnalytics";
import { useRememberedGuestInfo } from "@/hooks/useRememberedGuestInfo";
import { useTurnstile } from "@/hooks/useTurnstile";
import { TurnstileWidget } from "@/hooks/useTurnstile";
import type { StoreData } from "@/server-fns/booking/query";
import type { BookingIntent } from "@/types/booking-intent";
import type { DateYYYYMMDD, TimeHHMM } from "@/types/date";
import { totalGuestsFromForm } from "@/utils/partySize";
import { formatPartySizeMessage, resolvePartySizeLimits } from "@/utils/partySizeLimits";
import { parseStoreTime, storeNow } from "@/utils/storeTime";

import { BookingSuccess } from "./BookingSuccess";
import type { GuestBookingPayload } from "./BookingSummary";
import { BookingSummary } from "./BookingSummary";
import { PaymentStep } from "./PaymentStep";
import { getResumeSetupIntentClientSecret, readPendingBooking } from "./pendingBookingStorage";

type Step = "FORM" | "SUMMARY" | "PAYMENT" | "SUCCESS";

// Guard for entering the PAYMENT step: only when the store has an active
// cancellation-fee policy AND its Stripe Connect account is ACTIVE. This is
// defense in depth mirroring Task 4's admin UI gating — a fee toggle left on
// without an ACTIVE Connect account must never surface a payment step.
export const isCardCollectionRequired = (storeData: StoreData): boolean =>
    Boolean(
        storeData.bookingSettings?.cancellationFeeEnabled &&
        storeData.stripeConnectStatus === "ACTIVE",
    );

export type GuestBookingFormValues = {
    adults: number;
    kids: number;
    highchairs: number;
    date: dayjs.Dayjs;
    time: string;
    fullName: string;
    phoneNumber: string;
    email: string;
    note: string;
    // Set only when the store has two or more seating areas and the guest picked a time in one.
    // areaName is display-only (summary); areaId steers table assignment on the server.
    areaId?: number | null;
    areaName?: string;
};

const MAX_HIGHCHAIRS = 6;
const optionRange = (from: number, to: number) =>
    Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i);

const schema = Yup.object().shape({
    adults: Yup.number().required("Required").min(1, "At least one adult is required"),
    kids: Yup.number(),
    highchairs: Yup.number(),
    date: Yup.mixed()
        .required("Please select a date")
        .test("is-dayjs", "Please select a valid date", (value) => {
            return value && dayjs.isDayjs(value);
        }),
    time: Yup.string().required("Please select a time"),
    fullName: Yup.string().required("Required"),
    phoneNumber: Yup.string()
        .required("Required")
        .matches(/^[0-9]+$/, "Must be a number")
        .length(10, "Must be 10 digits"),
    email: Yup.string().required("Required").email(),
    note: Yup.string().max(500, "Must be 500 characters or less"),
});

type Props = {
    storeId: string;
    storeData: StoreData;
    initialValues?: BookingIntent | null;
    onBackToAI?: () => void;
};

// If the guest is landing back here from a Stripe 3DS/SCA redirect (see
// pendingBookingStorage.ts), resume straight into the PAYMENT step with the payload that was
// persisted before the redirect, instead of dropping them back on a blank FORM step. Guarded by
// isCardCollectionRequired too, in case the store's policy changed while the guest was away.
const getResumePendingPayload = (storeData: StoreData): GuestBookingPayload | null => {
    if (!isCardCollectionRequired(storeData)) return null;
    if (!getResumeSetupIntentClientSecret()) return null;
    return readPendingBooking()?.payload ?? null;
};

export const Form = ({ storeId, storeData, initialValues, onBackToAI }: Props) => {
    const [pendingPayload, setPendingPayload] = useState<GuestBookingPayload | null>(() =>
        getResumePendingPayload(storeData),
    );
    const [step, setStep] = useState<Step>(() => (pendingPayload ? "PAYMENT" : "FORM"));
    const [isAutoConfirmed, setIsAutoConfirmed] = useState(false);
    const [timeErrorOpen, setTimeErrorOpen] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const requiresCardCollection = isCardCollectionRequired(storeData);
    const { captchaToken, setCaptchaToken } = useTurnstile();
    const {
        rememberedInfo,
        isLoaded: isRememberedInfoLoaded,
        saveRememberedInfo,
        clearRememberedInfo,
    } = useRememberedGuestInfo();
    const { captureBookingFormSubmitted, captureSummaryViewed, captureBookingCompleted } =
        useBookingAnalytics(storeId);
    const contactInfoRef = useRef<HTMLDivElement>(null);
    const formTopRef = useRef<HTMLDivElement>(null);
    const didScrollRef = useRef(false);
    const prevDateRef = useRef<string | null>(null);

    const formikInitialValues = useMemo(() => {
        const initialDate = initialValues?.date
            ? dayjs(initialValues.date)
            : storeNow(storeData.timezone).add(
                  storeData.bookingSettings?.minAdvanceBookingDays ?? 1,
                  "day",
              );
        // Start the guest on the weekday's minimum so the form isn't invalid out of the box.
        const defaultAdults = Math.max(
            1,
            resolvePartySizeLimits(storeData.dayOfWeekPartySizes, initialDate.format("dddd")).min,
        );
        return {
            adults: initialValues?.adults || defaultAdults,
            kids: initialValues?.kids || 0,
            highchairs: initialValues?.highchairs || 0,
            date: initialDate,
            time: initialValues?.time || "",
            fullName: "",
            phoneNumber: "",
            email: "",
            note: initialValues?.note || "",
            areaId: undefined as number | null | undefined,
            areaName: undefined as string | undefined,
        };
    }, [initialValues]);

    // The weekday's limits only apply once a date is picked; before that the selects stay open
    // up to the widest day so nothing is hidden prematurely.
    const getPartySizeLimits = (date: unknown) => {
        if (dayjs.isDayjs(date)) {
            return resolvePartySizeLimits(storeData.dayOfWeekPartySizes, date.format("dddd"));
        }
        const widest = Math.max(
            resolvePartySizeLimits(undefined, "").max,
            ...storeData.dayOfWeekPartySizes.map((d) => d.maxPartySize),
        );
        return { min: 1, max: widest };
    };
    const getPartySizeError = (v: {
        date: unknown;
        adults: number;
        kids: number;
        highchairs: number;
    }) => {
        if (!dayjs.isDayjs(v.date)) return undefined;
        const limits = getPartySizeLimits(v.date);
        const total = totalGuestsFromForm(v);
        return total < limits.min || total > limits.max
            ? formatPartySizeMessage(limits, v.date.format("dddd"), total)
            : undefined;
    };

    const formik = useFormik({
        initialValues: formikInitialValues,
        enableReinitialize: true, // Ensured form state re-initializes when props change
        validationSchema: schema,
        validate: (vals) => {
            const partySizeError = getPartySizeError(vals);
            return partySizeError ? { adults: partySizeError } : {};
        },
        onSubmit: async (vals) => {
            if (step === "FORM") {
                const dateStr = dayjs.isDayjs(vals.date) ? vals.date.format("YYYY-MM-DD") : "";
                const bookingTime = parseStoreTime(`${dateStr} ${vals.time}`, storeData.timezone);
                const oneHourFromNow = storeNow(storeData.timezone).add(1, "hour");
                if (bookingTime.isBefore(oneHourFromNow)) {
                    setTimeErrorOpen(true);
                    return;
                }

                if (rememberMe) {
                    saveRememberedInfo({
                        fullName: vals.fullName,
                        phoneNumber: vals.phoneNumber,
                        email: vals.email,
                    });
                } else if (rememberedInfo) {
                    clearRememberedInfo();
                }

                captureBookingFormSubmitted({
                    adults: vals.adults,
                    kids: vals.kids,
                    date: dayjs.isDayjs(vals.date) ? vals.date.format("YYYY-MM-DD") : "",
                    time: vals.time,
                });
                setStep("SUMMARY");
                captureSummaryViewed();
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }
        },
    });

    const handleValidationComplete = useCallback(
        (isValid: boolean) => {
            if (didScrollRef.current || !initialValues) return;

            if (isValid) {
                contactInfoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
                formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            didScrollRef.current = true;
        },
        [initialValues],
    );

    useEffect(() => {
        if (initialValues && !didScrollRef.current) {
            const hasEssentialValues =
                initialValues.adults && initialValues.date && initialValues.time;
            if (!hasEssentialValues) {
                formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                didScrollRef.current = true;
            }
        }
    }, [initialValues]);

    const {
        errors,
        touched,
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setValues,
        isValid,
        dirty,
    } = formik;
    const partySizeLimits = getPartySizeLimits(values.date);
    const partySizeError = getPartySizeError(values);

    // One setValues instead of a setFieldValue per field: Formik validates each setFieldValue
    // against the values of the render it was called in, so back-to-back calls race and the last
    // validation can miss the others' changes (here: the time) and leave Continue disabled.
    const handleAreaSelection = useCallback(
        ({ time, areaId, areaName }: AreaSelection) =>
            setValues((prev) => ({ ...prev, time, areaId, areaName })),
        [setValues],
    );

    // Pre-fill contact fields from a previously "remembered" booking on this device, but never
    // clobber values the guest has already typed (or that came from elsewhere). Set all three
    // fields in one setValues call (not three setFieldValue calls) so a single validation pass
    // runs against the final values, instead of racing async validations against each other.
    useEffect(() => {
        if (!isRememberedInfoLoaded || !rememberedInfo) return;
        if (values.fullName || values.phoneNumber || values.email) return;
        setValues((prev) => ({
            ...prev,
            fullName: rememberedInfo.fullName,
            phoneNumber: rememberedInfo.phoneNumber,
            email: rememberedInfo.email,
        }));
        setRememberMe(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRememberedInfoLoaded, rememberedInfo]);

    // Clear time selection when date changes to prevent stale time values
    useEffect(() => {
        const currentDateStr =
            values.date && dayjs.isDayjs(values.date) ? values.date.format("YYYY-MM-DD") : null;

        // Only clear time if the date has actually changed (not on initial mount)
        if (prevDateRef.current !== null && prevDateRef.current !== currentDateStr) {
            setFieldValue("time", "", true);
        }

        prevDateRef.current = currentDateStr;
    }, [values.date, setFieldValue]);

    if (step === "SUCCESS") {
        return (
            <BookingSuccess
                email={values.email}
                isAutoConfirmed={isAutoConfirmed}
                onReturnHome={() => {
                    setStep("FORM");
                    setIsAutoConfirmed(false);
                    setPendingPayload(null);
                    formik.resetForm();
                    onBackToAI?.();
                }}
            />
        );
    }
    const handleBookingSuccess = (isConfirmed: boolean) => {
        captureBookingCompleted({
            is_auto_confirmed: isConfirmed,
            adults: values.adults,
            kids: values.kids,
        });
        setIsAutoConfirmed(isConfirmed);
        setStep("SUCCESS");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    if (step === "PAYMENT" && pendingPayload) {
        return (
            <PaymentStep
                payload={pendingPayload}
                storeData={storeData}
                captchaToken={captchaToken}
                onBack={() => setStep("SUMMARY")}
                onSuccess={handleBookingSuccess}
            />
        );
    }
    if (step === "SUMMARY") {
        return (
            <BookingSummary
                values={values}
                storeId={storeId}
                storeData={storeData}
                captchaToken={captchaToken}
                requiresCardCollection={requiresCardCollection}
                onBack={() => setStep("FORM")}
                onProceedToPayment={(payload) => {
                    setPendingPayload(payload);
                    setStep("PAYMENT");
                }}
                onSuccess={handleBookingSuccess}
            />
        );
    }

    return (
        <>
            <Box
                component={Card}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    position: "relative",
                }}
            >
                {onBackToAI && (
                    <Button
                        onClick={onBackToAI}
                        startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            mb: 3,
                            textTransform: "none",
                            bgcolor: "rgba(16, 185, 129, 0.1)",
                            color: "#10B981",
                            borderRadius: "20px",
                            px: 2,
                            py: 0.5,
                            fontSize: ".75rem",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "rgba(16, 185, 129, 0.2)",
                            },
                        }}
                    >
                        Try natural language
                    </Button>
                )}
                <Box ref={formTopRef}>
                    <Typography
                        variant="h4"
                        sx={{
                            marginBottom: 4,
                            fontWeight: 700,
                        }}
                    >
                        Make a booking
                    </Typography>
                </Box>
                <form onSubmit={handleSubmit} method="POST">
                    {/* CAPTCHA */}
                    <TurnstileWidget onTokenChange={setCaptchaToken} />
                    <Box
                        sx={{ display: "flex", gap: 2, marginBottom: 3 }}
                        flexDirection={["column", "row"]}
                    >
                        <FormControl fullWidth required>
                            <InputLabel id="adults-label">Adults</InputLabel>
                            <Select
                                labelId="adults-label"
                                value={values.adults}
                                onChange={handleChange}
                                label="Adults"
                                name="adults"
                            >
                                {optionRange(
                                    1,
                                    Math.max(
                                        1,
                                        partySizeLimits.max - values.kids - values.highchairs,
                                        values.adults,
                                    ),
                                ).map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="kids-label">Kids</InputLabel>
                            <Select
                                labelId="kids-label"
                                value={values.kids}
                                onChange={handleChange}
                                label="Kids"
                                name="kids"
                            >
                                {optionRange(
                                    0,
                                    Math.max(
                                        partySizeLimits.max - values.adults - values.highchairs,
                                        values.kids,
                                    ),
                                ).map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="highchairs-label">
                                <Box
                                    component="span"
                                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                >
                                    Highchairs
                                    <Tooltip
                                        title="Highchairs are counted separately and are not included in Kids."
                                        arrow
                                        placement="top"
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: 16, cursor: "help" }} />
                                    </Tooltip>
                                </Box>
                            </InputLabel>
                            <Select
                                labelId="highchairs-label"
                                id="highchairs"
                                value={values.highchairs}
                                onChange={handleChange}
                                label="Highchairs"
                                name="highchairs"
                            >
                                {optionRange(
                                    0,
                                    Math.max(
                                        Math.min(
                                            MAX_HIGHCHAIRS,
                                            partySizeLimits.max - values.adults - values.kids,
                                        ),
                                        values.highchairs,
                                    ),
                                ).map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <GuestTotalLine
                        adults={values.adults}
                        kids={values.kids}
                        highchairs={values.highchairs}
                    />
                    {dayjs.isDayjs(values.date) && (
                        <Typography
                            variant="caption"
                            color={partySizeError ? "error" : "text.secondary"}
                            role={partySizeError ? "alert" : undefined}
                            sx={{ display: "block", mb: 2 }}
                        >
                            {partySizeError ??
                                `${values.date.format("dddd")}s: ${partySizeLimits.min} to ${partySizeLimits.max} guests`}
                        </Typography>
                    )}
                    <Box
                        sx={{ display: "flex", gap: 2, marginBottom: 2.5 }}
                        flexDirection={["column", "row"]}
                    >
                        <FormControl fullWidth required>
                            <CustomDatePicker
                                label="Date"
                                value={values.date}
                                onChange={(value) => setFieldValue("date", value, true)}
                                minDate={storeNow(storeData.timezone).add(
                                    storeData.bookingSettings?.minAdvanceBookingDays ?? 1,
                                    "day",
                                )}
                                maxDate={storeNow(storeData.timezone).add(
                                    storeData.bookingSettings?.advanceBookingDays || 30,
                                    "days",
                                )}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ marginBottom: 6 }}>
                        {values.date && dayjs.isDayjs(values.date) ? (
                            <>
                                <AvailableTimes
                                    setFieldValue={setFieldValue}
                                    storeId={storeId}
                                    bookingDate={values.date.format("YYYY-MM-DD") as DateYYYYMMDD}
                                    dayOfWeek={values.date.format("dddd") as DayOfWeek}
                                    totalGuests={totalGuestsFromForm(values)}
                                    initialTime={values.time as TimeHHMM}
                                    onValidationComplete={handleValidationComplete}
                                    onSelectInArea={handleAreaSelection}
                                />
                                {!values.time && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 1.5, display: "block" }}
                                    >
                                        Please select a time slot above to continue
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="body1" color="error">
                                Please select a valid date
                            </Typography>
                        )}
                    </Box>
                    <Box ref={contactInfoRef}>
                        {initialValues?.date && initialValues?.time && initialValues?.adults && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    mb: 3,
                                    p: 2,
                                    borderRadius: "12px",
                                    bgcolor: "rgba(16, 185, 129, 0.05)",
                                    border: "1px dashed rgba(16, 185, 129, 0.2)",
                                }}
                            >
                                <AutoAwesomeIcon sx={{ color: "#10B981", fontSize: 20 }} />
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#065F46", fontWeight: 600 }}
                                >
                                    Great! We&apos;ve held your requested time. Just fill in your
                                    contact details to finish the booking.
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="h4" sx={{ marginBottom: 2 }}>
                            Contact information
                        </Typography>
                    </Box>
                    <Box
                        sx={{ display: "flex", gap: 2, marginBottom: 3 }}
                        flexDirection={["column", "row"]}
                    >
                        <TextField
                            fullWidth
                            required
                            label="Full name"
                            value={values.fullName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="fullName"
                            error={Boolean(touched.fullName && errors.fullName)}
                            helperText={touched.fullName && errors.fullName}
                        />
                    </Box>
                    <Box
                        sx={{ display: "flex", gap: 2, marginBottom: 3 }}
                        flexDirection={["column", "row"]}
                    >
                        <TextField
                            fullWidth
                            required
                            label="Phone number"
                            value={values.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            type="tel"
                            error={Boolean(
                                (touched.phoneNumber || !!values.phoneNumber) && errors.phoneNumber,
                            )}
                            helperText={
                                (touched.phoneNumber || !!values.phoneNumber) && errors.phoneNumber
                            }
                            name="phoneNumber"
                        />
                    </Box>
                    <Box
                        sx={{ display: "flex", gap: 2, marginBottom: 3 }}
                        flexDirection={["column", "row"]}
                    >
                        <TextField
                            fullWidth
                            required
                            label="Email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            type="email"
                            name="email"
                            error={Boolean(touched.email && errors.email)}
                            helperText={touched.email && errors.email}
                        />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, marginBottom: 1 }}>
                        <TextField
                            sx={{ width: "100%" }}
                            label="Note"
                            value={values.note}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="note"
                            multiline
                            rows={4}
                            error={Boolean(touched.note && errors.note)}
                            helperText={touched.note && errors.note}
                            placeholder={`Got any special requests? Leave it here!`}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1">
                                    Remember my details on this device
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    We&apos;ll pre-fill your contact details next time you book here
                                </Typography>
                            </Box>
                        }
                        sx={{ mb: 1, alignItems: "flex-start" }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ mt: 3 }}
                        disabled={!isValid || !dirty}
                    >
                        Continue
                    </Button>
                </form>
            </Box>
            <Snackbar
                open={timeErrorOpen}
                autoHideDuration={6000}
                onClose={() => setTimeErrorOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    onClose={() => setTimeErrorOpen(false)}
                    sx={{ width: "100%" }}
                >
                    The selected time must be at least 1 hour from now. Please choose a different
                    time.
                </Alert>
            </Snackbar>
        </>
    );
};
